import React from 'react';
import { Link } from 'react-router-dom';

interface AIInsight {
  id: number;
  type: 'strength' | 'weakness' | 'recommendation' | 'pattern' | 'achievement';
  icon: string;
  title: string;
  description: string;
  action?: {
    label: string;
    link: string;
  };
  color: string;
}

const AIInsights: React.FC = () => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  
  // Mock AI insights - will be replaced with actual AI analysis API
  const insights: AIInsight[] = [
    {
      id: 1,
      type: 'weakness',
      icon: 'fas fa-exclamation-triangle',
      title: 'Focus on Electrodynamics',
      description: 'Your accuracy in Electrodynamics is 65%, which is below your average. This topic has high weightage in JEE Advanced. Consider practicing 10 more questions daily.',
      action: {
        label: 'Practice Now',
        link: '/custom-quiz-history'
      },
      color: '#e74c3c'
    },
    {
      id: 2,
      type: 'strength',
      icon: 'fas fa-star',
      title: 'Excellent Progress in Organic Chemistry!',
      description: 'You\'ve improved by 15% in the last week! Your understanding of reaction mechanisms is strong. Keep up the great work.',
      color: '#27ae60'
    },
    {
      id: 3,
      type: 'pattern',
      icon: 'fas fa-chart-line',
      title: 'Best Performance in Mornings',
      description: 'Our analysis shows you solve Math problems 18% faster between 6-9 AM with 92% accuracy. Consider scheduling difficult topics during this time.',
      color: '#2d9cdb'
    },
    {
      id: 4,
      type: 'recommendation',
      icon: 'fas fa-lightbulb',
      title: 'Recommended: JEE Advanced Mock Test',
      description: 'Based on your current preparation level, taking a full-length mock test will help identify remaining gaps. Your predicted score: 240-260/360.',
      action: {
        label: 'Start Mock Test',
        link: '/mock-quiz'
      },
      color: '#f39c12'
    },
    {
      id: 5,
      type: 'achievement',
      icon: 'fas fa-trophy',
      title: 'Milestone Achieved!',
      description: 'You\'ve maintained 80%+ accuracy for 14 consecutive days. This consistency will significantly improve your rank.',
      color: '#667eea'
    }
  ];

  const getInsightBadge = (type: string) => {
    const badges = {
      strength: { label: 'Strength', bg: '#27ae60' },
      weakness: { label: 'Needs Focus', bg: '#e74c3c' },
      recommendation: { label: 'Recommended', bg: '#f39c12' },
      pattern: { label: 'Insight', bg: '#2d9cdb' },
      achievement: { label: 'Achievement', bg: '#667eea' }
    };
    const badge = badges[type as keyof typeof badges];
    return (
      <span className="badge rounded-pill px-3 py-1"
        style={{
          background: badge.bg + '15',
          color: badge.bg,
          fontSize: '11px',
          fontWeight: 600
        }}>
        {badge.label}
      </span>
    );
  };

  return (
    <div className="ai-insights-section mb-4">
      <div className="card border-0 shadow-sm" style={{ borderRadius: '20px' }}>
        <div 
          className="card-header bg-white border-0 p-3" 
          onClick={() => setIsExpanded(!isExpanded)}
          style={{ cursor: 'pointer' }}
        >
          {!isExpanded ? (
            <div className="d-flex align-items-center">
              <div 
                style={{
                  width: '48px',
                  height: '48px',
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '12px',
                  flexShrink: 0
                }}
              >
                <i className="fas fa-brain" style={{ fontSize: '24px', color: 'white' }}></i>
              </div>
              <div className="flex-grow-1">
                <h5 className="mb-0 text-dark" style={{ fontSize: '16px', fontWeight: '600' }}>AI Learning Insights</h5>
              </div>
              <span 
                className="badge rounded-pill px-3 py-1 fw-semibold me-2"
                style={{
                  background: '#ff5722',
                  color: 'white',
                  fontSize: '11px'
                }}
              >
                {insights.length} new
              </span>
              <div 
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  background: '#e8eaf6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                <i className="fas fa-chevron-down" style={{ color: '#667eea', fontSize: '11px' }}></i>
              </div>
            </div>
          ) : (
            <div className="d-flex align-items-center">
              <div 
                style={{
                  width: '48px',
                  height: '48px',
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '12px',
                  flexShrink: 0
                }}
              >
                <i className="fas fa-brain" style={{ fontSize: '24px', color: 'white' }}></i>
              </div>
              <div className="flex-grow-1">
                <h5 className="mb-0 text-dark" style={{ fontSize: '16px', fontWeight: '600' }}>AI Learning Insights</h5>
              </div>
              <div 
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  background: '#e8eaf6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <i className="fas fa-chevron-up" style={{ color: '#667eea', fontSize: '11px' }}></i>
              </div>
            </div>
          )}
        </div>

        {isExpanded && (
        <div className="card-body p-4 animate__animated animate__fadeIn">
          {/* AI Summary Box */}
          <div className="p-4 mb-4 rounded-4"
            style={{
              background: 'linear-gradient(135deg, #667eea15, #764ba215)',
              border: '2px solid #667eea20'
            }}>
            <div className="d-flex align-items-start">
              <div className="me-3">
                <div className="rounded-circle d-flex align-items-center justify-content-center"
                  style={{
                    width: '50px',
                    height: '50px',
                    background: 'linear-gradient(135deg, #667eea, #764ba2)',
                    color: 'white'
                  }}>
                  <i className="fas fa-brain fa-lg"></i>
                </div>
              </div>
              <div className="flex-grow-1">
                <h6 className="fw-bold mb-2">🎯 Your Learning Summary</h6>
                <p className="mb-0 text-muted">
                  Based on your recent performance across 45 tests, you're showing strong improvement in Physics and Math, 
                  but Chemistry needs more attention. Your study pattern is consistent, and you're on track to reach 
                  your target rank by March 2025.
                </p>
              </div>
            </div>
          </div>

          {/* Insights Grid */}
          <div className="row g-3">
            {insights.map((insight) => (
              <div key={insight.id} className="col-12">
                <div className="insight-card p-4 rounded-4"
                  style={{
                    background: 'white',
                    border: `2px solid ${insight.color}20`,
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = insight.color + '50';
                    e.currentTarget.style.boxShadow = `0 8px 20px ${insight.color}15`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = insight.color + '20';
                    e.currentTarget.style.boxShadow = 'none';
                  }}>
                  
                  <div className="d-flex align-items-start">
                    {/* Icon */}
                    <div className="me-3">
                      <div className="rounded-circle d-flex align-items-center justify-content-center"
                        style={{
                          width: '45px',
                          height: '45px',
                          background: insight.color + '15',
                          color: insight.color,
                          flexShrink: 0
                        }}>
                        <i className={insight.icon}></i>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-start justify-content-between mb-2">
                        <h6 className="fw-bold mb-0">{insight.title}</h6>
                        {getInsightBadge(insight.type)}
                      </div>
                      <p className="text-muted mb-3 small">
                        {insight.description}
                      </p>

                      {/* Action Button */}
                      {insight.action && (
                        <Link 
                          to={insight.action.link}
                          className="btn btn-sm rounded-pill px-4"
                          style={{
                            background: insight.color,
                            color: 'white',
                            border: 'none',
                            fontSize: '13px',
                            fontWeight: 600
                          }}>
                          <i className="fas fa-arrow-right me-2"></i>
                          {insight.action.label}
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Study Plan CTA */}
          <div className="mt-4 p-4 rounded-4 text-center"
            style={{
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              color: 'white'
            }}>
            <h5 className="fw-bold mb-2">Want a Personalized Study Plan?</h5>
            <p className="mb-3" style={{ opacity: 0.95 }}>
              Get AI-powered daily study schedules tailored to your strengths and weaknesses
            </p>
            <Link to="/study-plan" className="btn btn-light rounded-pill px-5 py-2 fw-semibold">
              <i className="fas fa-calendar-alt me-2"></i>
              Create My Study Plan
            </Link>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default AIInsights;
